import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, RotateCcw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

interface CalendarHeaderProps {
  currentWeekStart: Date;
  selectedWeekType: string;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  onToday: () => void;
  onWeekTypeChange: (value: string) => void;
  isCurrentWeek: boolean;
  className?: string;
  zoomLevel?: number;
  onZoomChange?: (value: number) => void;
}

export function CalendarHeader({
  currentWeekStart,
  selectedWeekType,
  onPreviousWeek,
  onNextWeek,
  onToday,
  onWeekTypeChange,
  isCurrentWeek,
  className = "",
  zoomLevel = 2,
  onZoomChange,
}: CalendarHeaderProps) {
  // Fonction pour obtenir le titre de la semaine
  const getWeekTitle = () => {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);

    const currentYear = new Date().getFullYear();
    const startYear = currentWeekStart.getFullYear();
    const endYear = weekEnd.getFullYear();

    const startMonth = currentWeekStart.toLocaleString("fr-FR", { month: "long" });
    const endMonth = weekEnd.toLocaleString("fr-FR", { month: "long" });

    let title = "";
    if (startMonth === endMonth) {
      title = startMonth.charAt(0).toUpperCase() + startMonth.slice(1);
    } else {
      title = `${startMonth.charAt(0).toUpperCase() + startMonth.slice(1)} - ${endMonth.charAt(0).toUpperCase() + endMonth.slice(1)}`;
    }

    // Ajouter l'année si différente de l'année actuelle
    if (startYear !== currentYear || endYear !== currentYear) {
      if (startYear === endYear) {
        title += ` ${startYear}`;
      } else {
        title += ` ${endYear}`;
      }
    }

    return title;
  };

  return (
    <div className={`flex items-center justify-between ${className}`}>
      <div className="flex items-center gap-x-2">
        {/* Fixed width to avoid slight layout shift between default/outline (border) & enabled/disabled states */}
        <Button
          variant={isCurrentWeek ? "outline" : "default"}
          size="sm"
          onClick={onToday}
          disabled={isCurrentWeek}
          className="flex w-[200px] items-center justify-center"
        >
          <RotateCcw size={16} className="mr-1" />
          Revenir à aujourd'hui
        </Button>

        <Button variant="ghost" size="icon" onClick={onPreviousWeek} className="flex items-center">
          <ChevronLeft size={24} className="mr-1" />
        </Button>

        <Button variant="ghost" size="icon" onClick={onNextWeek} className="flex items-center">
          <ChevronRight size={24} className="ml-1" />
        </Button>

        <h2 className="text-lg font-semibold text-gray-800">{getWeekTitle()}</h2>
      </div>

      <div className="flex items-center space-x-4">
        {/* Slider de zoom */}
        {onZoomChange && (
          <div className="flex items-center gap-x-2">
            <span className="text-sm text-gray-600">Zoom:</span>
            <Slider value={[zoomLevel]} onValueChange={(value) => onZoomChange(value[0])} min={0} max={4} step={1} className="w-32 hover:cursor-ew-resize" />
          </div>
        )}

        <Select value={selectedWeekType} onValueChange={onWeekTypeChange}>
          <SelectTrigger hidden className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="semaine">Semaine</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
